// Terminal view — primary dashboard. Companies-only for v1; ENG/AUTH metrics
// hidden. Displays treemap or table of companies sized by VOL or VEL,
// with Rising / Watchlist / Fading / Stats rails.

import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { tStyles, COLORS, fmtRaw } from "../styles";
import { api, AdminRequiredError, fmtMillsUsd, useHasAdminKey } from "../api";
import { useIsNarrow } from "../responsive";
import { nowAlignedToHourSec, type Aggregated } from "../aggregate";
import { Treemap, type TreemapItem } from "../components/Treemap";
import { TopicTable, type TableRow } from "../components/TopicTable";
import { CommandBar } from "../components/CommandBar";
import { DrillDown } from "../components/DrillDown";
import { StackedBars, type OverlaySeries } from "../components/StackedBars";
import type { CountBucket, QuerySpec, UserQueryItem } from "../../shared/types";

export type Tf = "24h" | "7d" | "30d";
export type Metric = "volume" | "velocity";
export type Layout = "treemap" | "table";

export interface CompanyTopic {
  name: string;
  spec: QuerySpec;
  buckets: CountBucket[];
  agg: Aggregated;
}

interface Props {
  topics: CompanyTopic[];
  // Ad-hoc user-typed comparison queries from the dashboard endpoint. Their
  // bucket data is already loaded; the side panel decides which to overlay.
  userQueryItems: UserQueryItem[];
  // exclusive end-of-window anchor for all metric math; null when no spec
  // has data within the freshness threshold (UI shows "● LIVE" instead).
  dataAsOf: number | null;
  // present so Terminal can ask App to refetch (e.g., after a backfill)
  onRefresh: () => void;
  onOpenLab: () => void;
}

export function Terminal({ topics, userQueryItems, dataAsOf, onRefresh, onOpenLab }: Props) {
  const narrow = useIsNarrow();
  const [metric, setMetric] = useState<Metric>("volume");
  const [tf, setTf] = useState<Tf>("24h");
  const [layout, setLayout] = useState<Layout>("treemap");
  const [selected, setSelected] = useState<CompanyTopic | null>(null);
  const [cursor, setCursor] = useState(0);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [watchlist, setWatchlist] = useState<string[]>(() => loadWatchlist());
  const [fontScale, setFontScale] = useState<number>(() => loadFontScale());
  const [enabledUserQueryIds, setEnabledUserQueryIds] = useState<number[]>(
    () => loadEnabledUserQueryIds(),
  );
  const [now, setNow] = useState(() => new Date());
  const [hoveredName, setHoveredName] = useState<string | null>(null);
  // Treemap drives Others membership; stacked-bars adopts the same set.
  const [treemapFoldedNames, setTreemapFoldedNames] = useState<string[]>([]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    saveWatchlist(watchlist);
  }, [watchlist]);

  useEffect(() => {
    saveFontScale(fontScale);
  }, [fontScale]);

  useEffect(() => {
    saveEnabledUserQueryIds(enabledUserQueryIds);
  }, [enabledUserQueryIds]);

  // Drop ids from the enabled set when their underlying user_query is gone
  // (e.g., admin deleted it in another tab). Otherwise localStorage drifts
  // and we'd quietly try to overlay a missing series.
  useEffect(() => {
    const present = new Set(userQueryItems.map((it) => it.user_query.id));
    setEnabledUserQueryIds((ids) => {
      const filtered = ids.filter((id) => present.has(id));
      return filtered.length === ids.length ? ids : filtered;
    });
  }, [userQueryItems]);

  const getValue = useCallback(
    (t: CompanyTopic): number => {
      if (metric === "volume") {
        return tf === "24h" ? t.agg.vol24 : tf === "7d" ? t.agg.vol7d : t.agg.vol30d;
      }
      // velocity metric: use absolute % so size encodes movement intensity
      return Math.abs(t.agg.velocity24 ?? 0);
    },
    [metric, tf],
  );

  const sorted = useMemo(
    () => [...topics].sort((a, b) => getValue(b) - getValue(a)),
    [topics, getValue],
  );

  const rising = useMemo(
    () =>
      [...topics]
        .filter((t) => (t.agg.velocity24 ?? 0) > 0)
        .sort((a, b) => (b.agg.velocity24 ?? 0) - (a.agg.velocity24 ?? 0))
        .slice(0, 8),
    [topics],
  );
  const fading = useMemo(
    () =>
      [...topics]
        .filter((t) => (t.agg.velocity24 ?? 0) < 0)
        .sort((a, b) => (a.agg.velocity24 ?? 0) - (b.agg.velocity24 ?? 0))
        .slice(0, 8),
    [topics],
  );

  const totalVol = useMemo(() => {
    return topics.reduce((s, t) => {
      const v = tf === "24h" ? t.agg.vol24 : tf === "7d" ? t.agg.vol7d : t.agg.vol30d;
      return s + v;
    }, 0);
  }, [topics, tf]);

  const toggleWatch = useCallback((name: string | undefined) => {
    if (!name) return;
    setWatchlist((w) => (w.includes(name) ? w.filter((x) => x !== name) : [...w, name]));
  }, []);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (cmdOpen) return;
      // Don't hijack typing in any focused input/textarea/contenteditable
      // (e.g. the COMPARE add-form, future filters). Without this, j/k/w/g/r
      // and friends gobble characters as the user types.
      const tgt = e.target as HTMLElement | null;
      if (tgt) {
        const tag = tgt.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || tgt.isContentEditable) {
          return;
        }
      }
      if (selected) {
        if (e.key === "Escape") {
          e.preventDefault();
          setSelected(null);
        }
        return;
      }
      if (e.key === "/") {
        e.preventDefault();
        setCmdOpen(true);
      } else if (e.key === "j" || e.key === "ArrowDown") {
        e.preventDefault();
        setCursor((c) => Math.min(sorted.length - 1, c + 1));
      } else if (e.key === "k" || e.key === "ArrowUp") {
        e.preventDefault();
        setCursor((c) => Math.max(0, c - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        setSelected(sorted[cursor] ?? null);
      } else if (e.key === "w") {
        e.preventDefault();
        toggleWatch(sorted[cursor]?.name);
      } else if (e.key === "g") {
        setLayout((l) => (l === "treemap" ? "table" : "treemap"));
      } else if (e.key === "r") {
        e.preventDefault();
        onRefresh();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cmdOpen, selected, cursor, sorted, toggleWatch, onRefresh]);

  const treemapItems: TreemapItem[] = useMemo(
    () =>
      sorted.map((t) => ({
        name: t.name,
        value: Math.max(1, getValue(t)),
        velocity: t.agg.velocity24,
        raw: tf === "24h" ? t.agg.vol24 : tf === "7d" ? t.agg.vol7d : t.agg.vol30d,
        spark7d: t.agg.spark7d,
        watched: watchlist.includes(t.name),
      })),
    [sorted, getValue, tf, watchlist],
  );

  const tableRows: TableRow[] = useMemo(
    () =>
      sorted.map((t) => ({
        name: t.name,
        raw: tf === "24h" ? t.agg.vol24 : tf === "7d" ? t.agg.vol7d : t.agg.vol30d,
        velocity: t.agg.velocity24,
        spark: t.agg.spark24,
        watched: watchlist.includes(t.name),
      })),
    [sorted, tf, watchlist],
  );

  const overlaySeries: OverlaySeries[] = useMemo(() => {
    if (enabledUserQueryIds.length === 0) return [];
    const enabled = new Set(enabledUserQueryIds);
    return userQueryItems
      .filter((it) => enabled.has(it.user_query.id))
      .map((it) => ({
        id: it.user_query.id,
        name: it.user_query.label,
        buckets: it.buckets,
      }));
  }, [enabledUserQueryIds, userQueryItems]);
  const overlayActive = overlaySeries.length > 0;

  const totalCoverage = topics.reduce((s, t) => s + t.agg.coverage24, 0);
  const maxCoverage = topics.length * 24;
  const coveragePct = maxCoverage === 0 ? 0 : Math.round((totalCoverage / maxCoverage) * 100);

  return (
    <div style={tStyles.root}>
      {/* Header */}
      <div
        style={{
          ...tStyles.headerBar,
          ...(narrow ? { flexWrap: "wrap", gap: 6, padding: "8px 10px" } : {}),
        }}
      >
        <div style={tStyles.brand}>
          <span style={tStyles.logoMark}>◼</span>
          <span style={tStyles.logoText}>MINDSHARE.X</span>
          <span style={tStyles.logoSlash}>::</span>
          <span style={tStyles.logoSub}>companies.{tf}.{metric}</span>
        </div>
        <div
          style={{
            ...tStyles.headerMeta,
            ...(narrow ? { gap: 6, flexWrap: "wrap" } : {}),
          }}
        >
          {!narrow && (
            <>
              <span style={tStyles.metaItem}>
                {now.toISOString().replace("T", " ").slice(0, 19)}Z
              </span>
              <span style={tStyles.metaPipe}>│</span>
            </>
          )}
          <span
            style={tStyles.metaLive}
            title={
              dataAsOf
                ? `metric windows anchored to last closed bucket: ${new Date(dataAsOf * 1000).toISOString()}`
                : "no fresh data yet — windows use wall-clock now"
            }
          >
            <span style={tStyles.metaDot}>●</span>
            {dataAsOf
              ? narrow
                ? `AS OF ${new Date(dataAsOf * 1000).toISOString().slice(5, 16).replace("T", " ")}Z`
                : `AS OF ${new Date(dataAsOf * 1000).toISOString().replace("T", " ").slice(0, 16)}Z`
              : "LIVE"}
          </span>
          <span style={tStyles.metaPipe}>│</span>
          <span style={tStyles.metaItem}>
            {fmtRaw(totalVol)} mentions / {tf}
          </span>
          {!narrow && (
            <>
              <span style={tStyles.metaPipe}>│</span>
              <span style={tStyles.metaItem}>{coveragePct}% coverage 24h</span>
            </>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div
        style={{
          ...tStyles.toolbar,
          ...(narrow
            ? {
                gap: 8,
                padding: "6px 10px",
                overflowX: "auto",
                flexWrap: "wrap",
                rowGap: 6,
              }
            : {}),
        }}
      >
        <div style={tStyles.tabGroup}>
          <button
            style={{ ...tStyles.tab, ...tStyles.tabActive }}
            onClick={() => {}}
          >
            <span style={tStyles.tabKey}>[1]</span>COMPANIES
          </button>
          {/* v1: hide MODELS / THEMES / PEOPLE; also hide on narrow viewports */}
          {!narrow && (
            <>
              <button
                style={{ ...tStyles.tab, ...tStyles.tabDisabled }}
                disabled
                title="not in v1"
              >
                <span style={tStyles.tabKey}>[2]</span>MODELS
              </button>
              <button
                style={{ ...tStyles.tab, ...tStyles.tabDisabled }}
                disabled
                title="not in v1"
              >
                <span style={tStyles.tabKey}>[3]</span>THEMES
              </button>
              <button
                style={{ ...tStyles.tab, ...tStyles.tabDisabled }}
                disabled
                title="not in v1"
              >
                <span style={tStyles.tabKey}>[4]</span>PEOPLE
              </button>
            </>
          )}
          <button
            style={tStyles.tab}
            onClick={onOpenLab}
            title="filter lab — compare engagement filters"
          >
            <span style={tStyles.tabKey}>[5]</span>LAB
          </button>
        </div>
        <div
          style={{
            ...tStyles.controlsRight,
            ...(narrow ? { gap: 6, flexWrap: "wrap" } : {}),
          }}
        >
          {!narrow && (
            <div style={tStyles.segGroup}>
              <span style={tStyles.segLabel}>METRIC</span>
              <button
                onClick={() => setMetric("volume")}
                style={{ ...tStyles.seg, ...(metric === "volume" ? tStyles.segActive : {}) }}
              >
                VOL
              </button>
              <button
                style={{ ...tStyles.seg, ...tStyles.segDisabled }}
                disabled
                title="needs tweet content (v2)"
              >
                ENG
              </button>
              <button
                style={{ ...tStyles.seg, ...tStyles.segDisabled }}
                disabled
                title="needs tweet content (v2)"
              >
                AUTH
              </button>
              <button
                onClick={() => setMetric("velocity")}
                style={{ ...tStyles.seg, ...(metric === "velocity" ? tStyles.segActive : {}) }}
              >
                VEL
              </button>
            </div>
          )}
          <div style={tStyles.segGroup}>
            <span style={tStyles.segLabel}>WIN</span>
            {(["24h", "7d", "30d"] as Tf[]).map((t) => (
              <button
                key={t}
                onClick={() => setTf(t)}
                style={{ ...tStyles.seg, ...(tf === t ? tStyles.segActive : {}) }}
              >
                {t.toUpperCase()}
              </button>
            ))}
          </div>
          <div style={tStyles.segGroup}>
            <span style={tStyles.segLabel}>VIEW</span>
            <button
              onClick={() => setLayout("treemap")}
              style={{ ...tStyles.seg, ...(layout === "treemap" ? tStyles.segActive : {}) }}
            >
              MAP
            </button>
            <button
              onClick={() => setLayout("table")}
              style={{ ...tStyles.seg, ...(layout === "table" ? tStyles.segActive : {}) }}
            >
              TBL
            </button>
          </div>
          {!narrow && (
            <div style={tStyles.segGroup}>
              <span style={tStyles.segLabel}>FONT</span>
              <input
                type="range"
                min={60}
                max={180}
                step={5}
                value={Math.round(fontScale * 100)}
                onChange={(e) => setFontScale(Number(e.target.value) / 100)}
                style={{
                  width: 70,
                  accentColor: COLORS.accent,
                  margin: "0 6px",
                }}
                title={`font scale ${Math.round(fontScale * 100)}%`}
              />
              <span
                style={{
                  fontSize: 10,
                  color: COLORS.textDim,
                  padding: "0 8px 0 0",
                  minWidth: 32,
                  fontFamily: "inherit",
                }}
              >
                {Math.round(fontScale * 100)}%
              </span>
            </div>
          )}
          <SyncButton onDone={onRefresh} />
          {!narrow && (
            <button onClick={() => setCmdOpen(true)} style={tStyles.cmdLaunch}>
              <span style={{ color: COLORS.textDimmer }}>/</span> search
              <span style={tStyles.kbd}>/</span>
            </button>
          )}
        </div>
      </div>

      {/* Main — desktop: 240px rail + 1fr center. Narrow: stacked column,
          center on top so the treemap is the headline; rail below scrolls
          inline with the page so users can swipe down to see Rising / Fading
          / Stats without losing the visualization. */}
      <div
        style={{
          ...tStyles.main,
          ...(narrow
            ? {
                display: "flex",
                flexDirection: "column",
                overflow: "auto",
              }
            : {}),
        }}
      >
        {/* LEFT rail — single sidebar with watchlist top, then rising, fading, stats.
            On narrow viewports we render this AFTER center via order:2. */}
        <div
          style={{
            ...tStyles.rail,
            ...(narrow ? { order: 2, borderRight: "none", overflow: "visible" } : {}),
          }}
        >
          <ComparePanel
            items={userQueryItems}
            enabledIds={enabledUserQueryIds}
            onToggle={(id) =>
              setEnabledUserQueryIds((ids) =>
                ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
              )
            }
            onChanged={onRefresh}
          />

          <div style={tStyles.railHeader}>
            <span style={tStyles.railTitleWatch}>★ WATCHLIST</span>
            <span style={tStyles.railCount}>{watchlist.length}</span>
          </div>
          <div style={tStyles.railList}>
            {watchlist.length === 0 && (
              <div
                style={{
                  color: COLORS.textGhost,
                  fontSize: 10,
                  padding: "4px 6px",
                  fontStyle: "italic",
                }}
              >
                press w on any company to add
              </div>
            )}
            {watchlist.map((name) => {
              const t = topics.find((x) => x.name === name);
              if (!t) return null;
              const v = t.agg.velocity24;
              return (
                <button key={name} onClick={() => setSelected(t)} style={tStyles.railRow}>
                  <span style={tStyles.railStar}>★</span>
                  <span style={tStyles.railName}>{t.name}</span>
                  <span style={v !== null && v < 0 ? tStyles.railVelDown : tStyles.railVelUp}>
                    {v === null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(1)}%`}
                  </span>
                </button>
              );
            })}
          </div>

          <div style={tStyles.railHeader}>
            <span style={tStyles.railTitle}>▲ RISING · {tf}</span>
            <span style={tStyles.railCount}>{rising.length}</span>
          </div>
          <div style={tStyles.railList}>
            {rising.map((t, i) => (
              <button key={t.name} onClick={() => setSelected(t)} style={tStyles.railRow}>
                <span style={tStyles.railIdx}>{String(i + 1).padStart(2, "0")}</span>
                <span style={tStyles.railName}>{t.name}</span>
                <span style={tStyles.railVelUp}>
                  {t.agg.velocity24 === null
                    ? "—"
                    : `+${t.agg.velocity24.toFixed(1)}%`}
                </span>
              </button>
            ))}
          </div>

          <div style={tStyles.railHeader}>
            <span style={tStyles.railTitleDown}>▼ FADING · {tf}</span>
            <span style={tStyles.railCount}>{fading.length}</span>
          </div>
          <div style={tStyles.railList}>
            {fading.map((t, i) => (
              <button key={t.name} onClick={() => setSelected(t)} style={tStyles.railRow}>
                <span style={tStyles.railIdx}>{String(i + 1).padStart(2, "0")}</span>
                <span style={tStyles.railName}>{t.name}</span>
                <span style={tStyles.railVelDown}>
                  {t.agg.velocity24 === null
                    ? "—"
                    : `${t.agg.velocity24.toFixed(1)}%`}
                </span>
              </button>
            ))}
          </div>

          <div style={{ marginTop: "auto" }}>
            <div style={tStyles.railHeader}>
              <span style={tStyles.railTitleStat}>· STATS · {tf.toUpperCase()}</span>
            </div>
            <div style={tStyles.statsBox}>
              <div style={tStyles.statRow}>
                <span style={tStyles.statK}>companies</span>
                <span style={tStyles.statV}>{topics.length}</span>
              </div>
              <div style={tStyles.statRow}>
                <span style={tStyles.statK}>mentions</span>
                <span style={tStyles.statV}>{fmtRaw(totalVol)}</span>
              </div>
              <div style={tStyles.statRow}>
                <span style={tStyles.statK}>rising</span>
                <span style={{ ...tStyles.statV, color: COLORS.up }}>
                  {topics.filter((t) => (t.agg.velocity24 ?? 0) > 0).length}
                </span>
              </div>
              <div style={tStyles.statRow}>
                <span style={tStyles.statK}>fading</span>
                <span style={{ ...tStyles.statV, color: COLORS.down }}>
                  {topics.filter((t) => (t.agg.velocity24 ?? 0) < 0).length}
                </span>
              </div>
              <div style={tStyles.statRow}>
                <span style={tStyles.statK}>coverage 24h</span>
                <span style={tStyles.statV}>{coveragePct}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* CENTER */}
        <div
          style={{
            ...tStyles.center,
            ...(narrow
              ? {
                  order: 1,
                  overflow: "visible",
                  flexShrink: 0,
                  minHeight: "70vh",
                  borderBottom: `1px solid ${COLORS.border}`,
                }
              : {}),
          }}
        >
          <div style={tStyles.centerHeader}>
            <span style={tStyles.centerTitle}>
              COMPANIES · {layout === "treemap" ? "TREEMAP" : "TABLE"} · {sorted.length} ROWS
            </span>
            <span style={tStyles.centerSub}>
              area = {metric} · color = velocity Δ24h
            </span>
          </div>
          {topics.length === 0 || totalCoverage === 0 ? (
            <EmptyState onRefresh={onRefresh} />
          ) : (
            <>
              {/* Chart sits above BOTH layouts — the COMPARE overlay belongs
                  to the chart, not to a particular layout, so toggling MAP↔TBL
                  shouldn't make user-query lines vanish. */}
              <StackedBars
                series={topics.map((t) => ({ name: t.name, buckets: t.buckets }))}
                days={30}
                endSec={nowAlignedToHourSec()}
                foldedNames={layout === "treemap" ? treemapFoldedNames : []}
                highlightName={hoveredName}
                onHover={setHoveredName}
                overlays={overlaySeries}
              />
              {layout === "treemap" ? (
                <Treemap
                  items={treemapItems}
                  selectedName={selected?.name ?? null}
                  cursorName={sorted[cursor]?.name ?? null}
                  fontScale={fontScale}
                  totalRaw={metric === "volume" ? totalVol : 0}
                  highlightName={hoveredName}
                  dimAll={overlayActive}
                  onHoverChange={setHoveredName}
                  onFoldedNamesChange={setTreemapFoldedNames}
                  onSelect={(name) => {
                    const idx = sorted.findIndex((s) => s.name === name);
                    if (idx >= 0) setCursor(idx);
                    const t = topics.find((s) => s.name === name);
                    if (t) setSelected(t);
                  }}
                />
              ) : (
                <TopicTable
                  rows={tableRows}
                  cursorIdx={cursor}
                  selectedName={selected?.name ?? null}
                  onSelect={(idx) => {
                    setCursor(idx);
                    setSelected(sorted[idx] ?? null);
                  }}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Footer — keyboard hints hidden on narrow (no keyboard); CTA + version
          stay visible. CTA copy shortens on narrow to fit. */}
      <div
        style={{
          ...tStyles.footer,
          ...(narrow ? { padding: "6px 10px", gap: 10, fontSize: 9 } : {}),
        }}
      >
        {!narrow && (
          <>
            <span><span style={tStyles.kbdInline}>j</span><span style={tStyles.kbdInline}>k</span> nav</span>
            <span><span style={tStyles.kbdInline}>↵</span> open</span>
            <span><span style={tStyles.kbdInline}>w</span> watch</span>
            <span><span style={tStyles.kbdInline}>g</span> view</span>
            <span><span style={tStyles.kbdInline}>r</span> refresh</span>
            <span><span style={tStyles.kbdInline}>/</span> search</span>
            <span><span style={tStyles.kbdInline}>esc</span> close</span>
          </>
        )}
        <span
          style={{
            marginLeft: narrow ? 0 : "auto",
            display: "flex",
            gap: narrow ? 8 : 16,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <a
            href="https://x.com/yifanbth"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: COLORS.accent,
              textDecoration: "none",
              borderBottom: `1px dashed ${COLORS.accent}`,
            }}
            title="Feedback / feature requests on X"
          >
            {narrow ? "built by @yifanbth →" : "built by @yifanbth · share / request features →"}
          </a>
          {!narrow && (
            <span style={{ opacity: 0.5 }}>
              v0.1.0 · companies-only · last hour {nowAlignedToHourSec()}
            </span>
          )}
        </span>
      </div>

      {/* Drill-down */}
      {selected && (
        <DrillDown
          topic={{ name: selected.name, spec: selected.spec, buckets: selected.buckets }}
          tf={tf}
          dataAsOf={dataAsOf}
          watched={watchlist.includes(selected.name)}
          onClose={() => setSelected(null)}
          onToggleWatch={() => toggleWatch(selected.name)}
        />
      )}

      {/* Command palette */}
      {cmdOpen && (
        <CommandBar
          topics={topics.map((t) => ({ name: t.name, hint: "company" }))}
          commands={[
            { label: "window 24h", run: () => setTf("24h") },
            { label: "window 7d", run: () => setTf("7d") },
            { label: "window 30d", run: () => setTf("30d") },
            { label: "metric volume", run: () => setMetric("volume") },
            { label: "metric velocity", run: () => setMetric("velocity") },
            { label: "view treemap", run: () => setLayout("treemap") },
            { label: "view table", run: () => setLayout("table") },
            { label: "open lab", run: onOpenLab },
            { label: "refresh", run: onRefresh },
          ]}
          onSelectTopic={(name) => {
            const t = topics.find((x) => x.name === name);
            if (t) setSelected(t);
          }}
          onClose={() => setCmdOpen(false)}
        />
      )}
    </div>
  );
}

// Toolbar SYNC button. Calls /api/cron/run via the existing admin auth and
// refreshes the dashboard on success. If no admin key is set, the request
// 401s and we tell the user to open the debug panel.
function SyncButton({ onDone }: { onDone: () => void }) {
  const hasAdmin = useHasAdminKey();
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "running" }
    | { kind: "done"; specs: number; calls: number; mills: number }
    | { kind: "error"; msg: string }
  >({ kind: "idle" });

  const click = async () => {
    if (state.kind === "running") return;
    setState({ kind: "running" });
    try {
      const r = await api.cronRun();
      if (r.errors.length > 0) {
        setState({ kind: "error", msg: r.errors[0] ?? "unknown" });
        return;
      }
      setState({
        kind: "done",
        specs: r.specs,
        calls: r.api_calls,
        mills: r.cost_mills,
      });
      onDone();
    } catch (e) {
      const msg =
        e instanceof AdminRequiredError
          ? "admin key required (Ctrl+D)"
          : e instanceof Error
            ? e.message
            : String(e);
      setState({ kind: "error", msg });
    }
  };

  if (!hasAdmin) return null;

  let label: string;
  let title: string;
  if (state.kind === "idle") {
    label = "↻ SYNC 7d";
    title = "POST /api/cron/run — fills any gaps in the last 7d for every active company via /counts/recent ($0.005/spec)";
  } else if (state.kind === "running") {
    label = "syncing…";
    title = "fetching from X API";
  } else if (state.kind === "done") {
    if (state.calls === 0) {
      label = "✓ up to date";
    } else {
      label = `✓ ${state.specs} specs · ${fmtMillsUsd(state.mills)}`;
    }
    title = `${state.specs} specs · ${state.calls} calls · ${fmtMillsUsd(state.mills)}`;
  } else {
    label = `✗ ${state.msg}`;
    title = state.msg;
  }

  return (
    <button
      onClick={click}
      title={title}
      disabled={state.kind === "running"}
      style={{
        ...tStyles.cmdLaunch,
        color: state.kind === "error" ? COLORS.down : COLORS.accent,
        opacity: state.kind === "running" ? 0.6 : 1,
        cursor: state.kind === "running" ? "wait" : "pointer",
      }}
    >
      {label}
    </button>
  );
}

function EmptyState({ onRefresh }: { onRefresh: () => void }) {
  const hasAdmin = useHasAdminKey();
  return (
    <div style={tStyles.emptyBanner}>
      <div style={tStyles.emptyTitle}>NO BUCKETS CACHED</div>
      <div>
        {hasAdmin ? (
          <>
            D1 is empty. Click <code>↻ SYNC 7d</code> in the toolbar, or run a
            backfill from the drill-down on any company.
          </>
        ) : (
          <>Cache is empty. Check back later — admin will populate it.</>
        )}
      </div>
      <button style={{ ...tStyles.drillButton, marginTop: 16 }} onClick={onRefresh}>
        ⟳ refresh dashboard
      </button>
    </div>
  );
}

const WATCHLIST_KEY = "mindsharex.watchlist";
function loadWatchlist(): string[] {
  try {
    const raw = localStorage.getItem(WATCHLIST_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}
function saveWatchlist(w: string[]) {
  try {
    localStorage.setItem(WATCHLIST_KEY, JSON.stringify(w));
  } catch {
    /* ignore */
  }
}

const ENABLED_USER_QUERY_IDS_KEY = "mindsharex.enabledUserQueryIds";
function loadEnabledUserQueryIds(): number[] {
  try {
    const raw = localStorage.getItem(ENABLED_USER_QUERY_IDS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((x) => typeof x === "number" && Number.isFinite(x))
      : [];
  } catch {
    return [];
  }
}
function saveEnabledUserQueryIds(ids: number[]) {
  try {
    localStorage.setItem(ENABLED_USER_QUERY_IDS_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

// COMPARE side-panel section. Renders user-typed comparison queries with a
// per-row toggle (visible to all readers) and admin-only add/delete.
//
// Rationale: keeps ad-hoc lookups clearly separate from the curated
// companies (★ WATCHLIST + ▲ RISING + ▼ FADING all live below this), so a
// public viewer can see what someone has been comparing against without the
// list getting confused with the editorial set.
function ComparePanel({
  items,
  enabledIds,
  onToggle,
  onChanged,
}: {
  items: UserQueryItem[];
  enabledIds: number[];
  onToggle: (id: number) => void;
  onChanged: () => void;
}) {
  const hasAdmin = useHasAdminKey();
  const [addOpen, setAddOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [rawQuery, setRawQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const enabledSet = new Set(enabledIds);

  const onSubmit = async () => {
    if (!label.trim() || !rawQuery.trim() || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.createUserQuery(label.trim(), rawQuery.trim());
      // The row is created even if the X backfill fails (so the user can
      // retry / delete cleanly), but a silent backfill failure looks like
      // "0 mentions" in the UI — surface it explicitly here.
      if (r.backfill && r.backfill.errors.length > 0) {
        setErr(`row created, but X backfill failed: ${r.backfill.errors[0]}`);
        onChanged();
        return;
      }
      setLabel("");
      setRawQuery("");
      setAddOpen(false);
      onChanged();
    } catch (e) {
      setErr(
        e instanceof AdminRequiredError
          ? "admin key required (Ctrl+D)"
          : e instanceof Error
            ? e.message
            : String(e),
      );
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id: number) => {
    if (!hasAdmin) return;
    if (!confirm("Remove this comparison query? (data is kept in cache)")) return;
    try {
      await api.deleteUserQuery(id);
      onChanged();
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    }
  };

  // Hide the entire section from public viewers when there's nothing to show
  // — no items + no admin key means the panel would be empty.
  if (items.length === 0 && !hasAdmin) return null;

  return (
    <>
      <div style={tStyles.railHeader}>
        <span style={{ ...tStyles.railTitle, color: COLORS.accent }}>
          ◇ COMPARE
        </span>
        <span style={tStyles.railCount}>{items.length}</span>
      </div>
      <div style={tStyles.railList}>
        {items.length === 0 && (
          <div
            style={{
              color: COLORS.textGhost,
              fontSize: 10,
              padding: "4px 6px",
              fontStyle: "italic",
            }}
          >
            no comparison queries yet
          </div>
        )}
        {items.map((it) => {
          const id = it.user_query.id;
          const on = enabledSet.has(id);
          const total = it.buckets.reduce((s, b) => s + b.tweet_count, 0);
          return (
            <div
              key={id}
              style={{
                ...tStyles.railRow,
                cursor: "default",
                gap: 6,
              }}
            >
              <input
                type="checkbox"
                checked={on}
                onChange={() => onToggle(id)}
                style={{
                  accentColor: COLORS.accent,
                  cursor: "pointer",
                  margin: 0,
                  flexShrink: 0,
                }}
                title={on ? "hide overlay" : "show on chart"}
              />
              <span
                style={{
                  ...tStyles.railName,
                  color: on ? COLORS.text : COLORS.textDim,
                  cursor: "pointer",
                }}
                onClick={() => onToggle(id)}
                title={it.user_query.raw_query}
              >
                {it.user_query.label}
              </span>
              <span
                style={{
                  color: COLORS.textDimmer,
                  fontSize: 10,
                  fontVariantNumeric: "tabular-nums",
                }}
                title={`${total.toLocaleString()} mentions over the cached window`}
              >
                {fmtRaw(total)}
              </span>
              {hasAdmin && (
                <button
                  onClick={() => onDelete(id)}
                  title="delete query"
                  style={{
                    background: "transparent",
                    border: "none",
                    color: COLORS.textGhost,
                    cursor: "pointer",
                    fontSize: 11,
                    padding: "0 2px",
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          );
        })}
        {hasAdmin && !addOpen && (
          <button
            onClick={() => setAddOpen(true)}
            style={{
              ...tStyles.railRow,
              color: COLORS.accent,
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            + add comparison
          </button>
        )}
        {hasAdmin && addOpen && (
          <div
            style={{
              padding: "6px",
              border: `1px dashed ${COLORS.border}`,
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              gap: 4,
              marginTop: 4,
            }}
          >
            <input
              type="text"
              placeholder="label (e.g. gstack/gbrain)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              style={addInputStyle}
              autoFocus
            />
            <input
              type="text"
              placeholder='X query (e.g. gstack OR gbrain)'
              value={rawQuery}
              onChange={(e) => setRawQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSubmit();
                if (e.key === "Escape") {
                  setAddOpen(false);
                  setErr(null);
                }
              }}
              style={addInputStyle}
            />
            {err && (
              <div style={{ color: COLORS.down, fontSize: 10 }}>{err}</div>
            )}
            <div style={{ display: "flex", gap: 4 }}>
              <button
                onClick={onSubmit}
                disabled={busy || !label.trim() || !rawQuery.trim()}
                style={{
                  flex: 1,
                  background: "transparent",
                  border: `1px solid ${COLORS.accent}`,
                  color: COLORS.accent,
                  padding: "4px 8px",
                  fontFamily: "inherit",
                  fontSize: 10,
                  letterSpacing: 1,
                  cursor: busy ? "wait" : "pointer",
                  opacity: busy || !label.trim() || !rawQuery.trim() ? 0.5 : 1,
                }}
              >
                {busy ? "fetching 30d…" : "ADD + BACKFILL"}
              </button>
              <button
                onClick={() => {
                  setAddOpen(false);
                  setErr(null);
                }}
                style={{
                  background: "transparent",
                  border: `1px solid ${COLORS.border}`,
                  color: COLORS.textDim,
                  padding: "4px 8px",
                  fontFamily: "inherit",
                  fontSize: 10,
                  cursor: "pointer",
                }}
              >
                cancel
              </button>
            </div>
            <div
              style={{
                color: COLORS.textDimmer,
                fontSize: 9,
                lineHeight: 1.4,
              }}
            >
              creates a new chart overlay using the default filter preset.
              backfills last 30d immediately (~$0.01 from /counts/all).
            </div>
          </div>
        )}
      </div>
    </>
  );
}

const addInputStyle: CSSProperties = {
  background: COLORS.bg,
  border: `1px solid ${COLORS.border}`,
  color: COLORS.text,
  padding: "4px 6px",
  fontFamily: "inherit",
  fontSize: 11,
  outline: "none",
};

const FONT_SCALE_KEY = "mindsharex.fontscale";
function loadFontScale(): number {
  try {
    const raw = localStorage.getItem(FONT_SCALE_KEY);
    if (!raw) return 1;
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0.4 && n <= 2 ? n : 1;
  } catch {
    return 1;
  }
}
function saveFontScale(s: number) {
  try {
    localStorage.setItem(FONT_SCALE_KEY, String(s));
  } catch {
    /* ignore */
  }
}
